import { useEffect } from 'react';

import ReturnCostlyCustomerWarning from './ReturnCostlyCustomerWarning';
import ReturnStoreCredit from './ReturnStoreCredit';

import { ZawWarningBanner } from 'components/account/Returns/ZawWarningBanner';
import { cn } from 'helpers/classnames';
import AccountBreadcrumbs from 'components/account/AccountBreadcrumbs';
import ReturnLegalVerbiage from 'components/account/Returns/ReturnLegalVerbiage';
import { ReturnShippingHandler } from 'components/account/Returns/ReturnShippingHandler';
import SubmitReturn from 'components/account/Returns/SubmitReturn';
import ReturnErrorMessage from 'components/account/Returns/ReturnErrorMessage';
import ZawInfoBanner from 'components/account/Returns/ZawInfoBanner';
import { pluralize } from 'helpers/index';
import { PROVIDE_LABEL_OPTIONS, PROVIDE_REFUND_OPTIONS } from 'constants/exchanges';
import ReturnsUtils from 'helpers/ReturnsUtils';
import returnsUtils, { RETURN_REFUND_VERBIAGE } from 'utils/returns';
import useMyAccountRedesignEnabled from 'hooks/useMyAccountRedesignEnabled';
import { isAfterPayOrder } from 'helpers/PaymentUtils';
import { Alert } from 'components/common/Alert/Alert';

// eslint-disable-next-line css-modules/no-unused-class
import css from 'styles/containers/returns/returns.scss';

export const ReturnStepInitiate = ({
  testId,
  marketplace,
  isWithinReturnWindow,
  labelDelivery,
  labelPaymentType,
  zawSubsidyReissueEligibility,
  CLTPhoneNumber,
  UPSPickUpAddress,
  isZAWReturnsEnabled,
  isStoreCreditEnabled,
  breadcrumbsLinks,
  makeWhiteLabelRefundSummary,
  makeRefundSummary,
  onLabelDeliverySelect,
  onLabelPaymentTypeSelect,
  onCancelReturn,
  onSubmitReturn,
  isFlaggedAsCostlyCustomer,
  onCheckTermsOfReturn,
  acceptTermsOfReturn,
  isGuest,
  ...props
}) => {
  const { returns, isShipmentOptionsError, isRepetitionConflictError, keepExchangeOrdersFromReturnCancel, isShipmentOptionsLoading } = props;
  const { boxInfo, isReturnPreventingSubmitError, isReturnError, returnErrorType, isReturnSubmitting } = returns;
  const {
    isPrivateLabel, // isPrivateLabel is a marketplace toggle that toggles the layout
    account: { cltLink },
    returns: { returnPolicyUrl, errorMessages, handlesReturnShipping, showReturnInstructionsBoxes },
    checkout: {
      helpUrl: { conditionsOfUse }
    }
  } = marketplace;

  const isMyAccRedesign = useMyAccountRedesignEnabled();

  const cancelButtonText = isRepetitionConflictError ? 'Back' : 'Cancel';

  const {
    pricing: { totalCharged },
    // becomes undefined when EGC only payment
    paymentMethod
  } = props.order;
  const afterPayOrder = paymentMethod?.issuingBank && isAfterPayOrder(paymentMethod.issuingBank);

  const zeroOriginalPaymentBalance = ReturnsUtils.isZeroAmount(totalCharged);
  const isSubmitDisabled = ReturnsUtils.isSubmitDisabled([
    isReturnSubmitting,
    isReturnError,
    isReturnPreventingSubmitError,
    isShipmentOptionsError,
    isRepetitionConflictError,
    isShipmentOptionsLoading,
    !labelDelivery,
    isStoreCreditEnabled && !labelPaymentType && !zeroOriginalPaymentBalance && !afterPayOrder
  ]);

  useEffect(() => {
    if (isStoreCreditEnabled && !isGuest && !isWithinReturnWindow) {
      onLabelPaymentTypeSelect(PROVIDE_REFUND_OPTIONS.STORE_CREDIT);
    }
  }, [isStoreCreditEnabled, isWithinReturnWindow, onLabelPaymentTypeSelect]);

  return (
    <div className={cn(css.container, { [css.addTopMargin]: !isMyAccRedesign })}>
      <AccountBreadcrumbs links={breadcrumbsLinks} className={css.breadcrumb} currentText="Return Items" />

      <MainHeading isPrivateLabel={isPrivateLabel} boxInfo={boxInfo} testId={testId} />

      <ReturnErrorMessage
        phoneNumber={CLTPhoneNumber}
        isReturnError={isReturnError || isReturnPreventingSubmitError}
        returnErrorType={returnErrorType}
        mixedReturnMessage={errorMessages?.mixedReturnsErrorMessage}
      />

      <ZawInfoBanner enabled={isZAWReturnsEnabled} zawSubsidyReissueEligibility={zawSubsidyReissueEligibility} isConfirmationPage={false} />
      {isZAWReturnsEnabled && <ZawWarningBanner zawSubsidyReissueEligibility={zawSubsidyReissueEligibility} />}

      {isPrivateLabel
        ? makeWhiteLabelRefundSummary({
            useCustomerReturnableFlag: keepExchangeOrdersFromReturnCancel,
            isConfirmationOrder: false
          })
        : makeRefundSummary({ useCustomerReturnableFlag: keepExchangeOrdersFromReturnCancel })}

      {!isPrivateLabel && !showReturnInstructionsBoxes && <ReturnLegalVerbiage returnPolicyUrl={returnPolicyUrl} />}

      {handlesReturnShipping ? (
        <div className={cn(css.labelDelivery, showReturnInstructionsBoxes && css.returnMethodSection)}>
          {/* non guest mixed or full payment thats not after pay */}
          {isStoreCreditEnabled && !isGuest && isWithinReturnWindow && !afterPayOrder && !zeroOriginalPaymentBalance && !marketplace.is6pm && (
            <div className={css.toastContainer}>
              <ReturnStoreCredit onLabelPaymentTypeSelect={onLabelPaymentTypeSelect} labelPaymentType={labelPaymentType} />
            </div>
          )}
          <ReturnShippingHandler
            {...props}
            isStoreCreditEnabled={isStoreCreditEnabled}
            labelPaymentType={labelPaymentType}
            zeroOriginalPaymentBalance={zeroOriginalPaymentBalance}
            afterPayOrder={afterPayOrder}
            marketplace={marketplace}
            labelDelivery={labelDelivery}
            UPSPickUpAddress={UPSPickUpAddress}
            onLabelDeliverySelect={onLabelDeliverySelect}
            onCancelReturn={onCancelReturn}
            onSubmitReturn={onSubmitReturn}
          />
          {/* past window or full gift card payment */}
          {isStoreCreditEnabled && !isGuest && (!isWithinReturnWindow || zeroOriginalPaymentBalance) && (
            <div className="mb-4 text-sm sm:mt-8 md:mt-1">Refund will be issued to store credit.</div>
          )}
          {/* after pay and must be within return window */}
          {isStoreCreditEnabled && isWithinReturnWindow && afterPayOrder && (
            <div className="mb-4 text-sm sm:mt-8 md:mt-1">Refund will be issued to original form of payment.</div>
          )}
          <div className={css.toastContainer}>
            <Alert variant="info" msg={RETURN_REFUND_VERBIAGE} isDismissible={false} />
            {isFlaggedAsCostlyCustomer && (
              <ReturnCostlyCustomerWarning
                acceptTermsOfReturn={acceptTermsOfReturn}
                onCheckTermsOfReturn={onCheckTermsOfReturn}
                isReturnPreventingSubmitError={isReturnPreventingSubmitError}
                returnErrorType={returnErrorType}
                returnPolicyUrl={returnPolicyUrl}
                cltLink={cltLink}
                conditionsOfUse={conditionsOfUse}
              />
            )}
          </div>

          {labelDelivery !== PROVIDE_LABEL_OPTIONS.MAIL && (
            <div className={css.submitReturn}>
              <SubmitReturn
                onCancelReturn={onCancelReturn}
                onSubmitReturn={onSubmitReturn}
                cancelButtonText={cancelButtonText}
                isSubmitDisabled={isSubmitDisabled}
              />
            </div>
          )}
        </div>
      ) : (
        <div>
          <ReturnCautionMessage cltLink={cltLink} />
          <SubmitReturn
            onCancelReturn={onCancelReturn}
            onSubmitReturn={onSubmitReturn}
            cancelButtonText={cancelButtonText}
            isSubmitDisabled={isSubmitDisabled}
          />
        </div>
      )}
    </div>
  );
};

const MainHeading = ({ isPrivateLabel, boxInfo, testId }) => {
  const totalNumberOfItems = returnsUtils.countTotalNumberOfItems(boxInfo);
  return isPrivateLabel ? (
    <h1 className={css.privateLabelTitle}>
      Returns {totalNumberOfItems} {pluralize('item', totalNumberOfItems)}
    </h1>
  ) : (
    <h1 className="zappos:heading-xl" data-test-id={testId('h1')}>
      Return Items
    </h1>
  );
};

export const ReturnCautionMessage = ({ cltLink }) => (
  <p className={css.returnCautionMessage6pm}>
    If you believe you have received defective or incorrect merchandise, please <a href={cltLink}>contact us</a> before proceeding.
  </p>
);
